import useUserLanguage from '@hooks/useUserLanguage'
import { I18NProxy } from '@proxies/I18N'
import { AutenticateProxy, cleanAuthToken } from '@proxies/auth'
import { getProxyUrl } from '@proxies/index'
import { setResource, useAppStore } from '@store'
import { Store } from '@types'
import { getLanguageID } from '@utils/getLanguageID'
import { translateApiError } from '@utils/translateApiError'
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react'

type LoginData = {
  password: string
  correo: string
  remember: boolean
}

type UpdateUserData = UserType & {
  PasswordActual?: string
  PasswordNueva?: string
  PasswordNuevaConfirmar?: string
}

export type UserType = {
  nombreCultura: string
  idIdioma: number
  nombre: string
  apellidos: string
  direccion: string
  pais: string
  ciudad: string
  codigoPostal: string
  telefono: string
  correo: string
  empresa: string
  cif: string
  idiomaCultura: string
  bloquearEdicion?: boolean
}

type SetUserType = React.Dispatch<React.SetStateAction<UserType>>
type State = [UserType, SetUserType]

const userDefaultValue = {
  name: 'Public',
  nombreCultura: 'ES',
  idIdioma: 1,
  nombre: 'Public',
  apellidos: '',
  direccion: '',
  pais: '',
  ciudad: '',
  codigoPostal: '',
  telefono: '',
  correo: '',
  empresa: '',
  cif: '',
  idiomaCultura: 'ES',
  tipoUsuario: 'P',
}

const userContext = createContext<State>(null!)

async function getIdiomas() {
  const idiomas = await new I18NProxy().getIdiomas()
  const setLanguageOptions = useAppStore.getState().setLanguageOptions
  window.localStorage.setItem('languageOptions', JSON.stringify(idiomas))
  setLanguageOptions(idiomas)
}

export function UserContextProvider({ children }: { children: ReactNode }) {
  const setTextos = useAppStore.use.setTextos()
  const setPrefs = useAppStore.use.setPrefs()
  const setTextures = useAppStore.use.setTextures()
  const setImpuestos = useAppStore.use.setImpuestos()
  const setCameraPosition = useAppStore.use.setCameraPosition()

  const userState = useState(() => {
    const item = window.localStorage.getItem('userData')
    if (!item) {
      window.localStorage.setItem('userData', JSON.stringify(userDefaultValue))
      return userDefaultValue
    }
    return JSON.parse(item) as UserType
  })

  useEffect(() => {
    const authProxy = new AutenticateProxy()
    setResource('textos')
    setResource('Walls', !window.location.href.includes('planner'))
    setResource('prefs')
    authProxy.newSession().then(({ idioma, textos, prefs, prefsInfo, impuestos, ...rest }: any) => {
      setTextos({ idioma, textos })
      setResource('textos', true)
      getIdiomas()
      setPrefs(prefs, prefsInfo)
      setImpuestos(impuestos)

      // Restaurar permisos del usuario desde la respuesta o desde localStorage
      const userData = window.localStorage.getItem('userData')
      let userPerms = rest // Intentar desde la respuesta del backend primero

      if (!userPerms?.verPrecios && userData) {
        try {
          userPerms = JSON.parse(userData)
        } catch (e) {
          console.error('[USER CONTEXT] Error parseando userData:', e)
        }
      }

      // Setear permisos en el store global
      if (userPerms) {
        useAppStore.getState().setIdTiendas(userPerms.idTiendas ?? null)
        useAppStore.getState().setDisVerdis(userPerms.disVerdis ?? false)
        useAppStore.getState().setDisVerUsu(userPerms.disVerUsu ?? false)
        useAppStore.getState().setDisVerGeneral(userPerms.disVerGeneral ?? false)
        useAppStore.getState().setVerPrecios(userPerms.verPrecios ?? false)
        useAppStore.getState().setAdmModPermisos(userPerms.admModPermisos ?? false)
        useAppStore.getState().setAsignarDisenyosUf(userPerms.asignarDisenyosUf ?? false)
      }

      const body = document.querySelector('body')
      body.style.setProperty('--color1', prefs?.['C/Color1'])
      body.style.setProperty('--color2', prefs?.['C/Color2'])
      body.style.setProperty('--color3', prefs?.['C/Color3'])
      body.style.setProperty('--color4', prefs?.['C/Color4'])
      body.style.setProperty('--color5', prefs?.['C/Color5'])
      body.style.setProperty('--color6', prefs?.['C/Color6'])
      body.style.setProperty('--color7', prefs?.['C/Color7'])
      body.style.setProperty('--color8', prefs?.['C/Color8'])
      body.style.setProperty('--color9', prefs?.['C/Color9'])
      body.style.setProperty('--color10', prefs?.['C/GIZMOX'])
      body.style.setProperty('--color11', prefs?.['C/GIZMOY'])
      body.style.setProperty('--color12', prefs?.['C/GIZMOZ'])
      body.style.setProperty('--color13', prefs?.['C/Color10'])
      body.style.setProperty('--color14', prefs?.['C/HoverMenu'])
      body.style.setProperty('--color15', prefs?.['C/FondoBuscarMenu'])
      body.style.setProperty('--color16', prefs?.['C/ContornoMenu'])
      body.style.setProperty('--color17', prefs?.['C/ContornoMenuSel'])
      body.style.setProperty('--color18', prefs?.['C/ContornoSubM'])
      body.style.setProperty('--color19', prefs?.['C/ContornoSubMSel'])
      body.style.setProperty('--color20', prefs?.['C/CirculoVertices'])
      body.style.setProperty('--urlBgIni', `url(${prefs?.FONDOCLIINICIAL})`)
      body.style.setProperty('--urlBgLog', `url(${prefs?.FONDOCLILOGIN})`)
      body.style.setProperty('--urlBgInfo', `url(${prefs?.FONDOCLITUTORIAL})`)
      body.style.setProperty('--color21', prefs?.['C/ColorC01'])
      body.style.setProperty('--color22', prefs?.['C/ColorC02'])
      body.style.setProperty('--color23', prefs?.['C/ColorC03'])
      body.style.setProperty('--color24', prefs?.['C/ColorC04'])
      body.style.setProperty('--color25', prefs?.['C/ColorC05'])
      body.style.setProperty('--color26', prefs?.['C/ColorC06'])
      body.style.setProperty('--color27', prefs?.['C/ColorC07'])
      body.style.setProperty('--color28', prefs?.['C/ColorC08'])
      body.style.setProperty('--color29', prefs?.['C/ColorC09'])
      body.style.setProperty('--color30', prefs?.['C/ColorC10'])
      body.style.setProperty('--color31', prefs?.['C/ColorC11'])
      body.style.setProperty('--color32', prefs?.['C/ColorD01'])
      body.style.setProperty('--color33', prefs?.['C/ColorD02'])
      body.style.setProperty('--color34', prefs?.['C/ColorD03'])
      body.style.setProperty('--color35', prefs?.['C/ColorD04'])
      body.style.setProperty('--color36', prefs?.['C/ColorD05'])
      body.style.setProperty('--color37', prefs?.['C/ColorD06'])
      body.style.setProperty('--color38', prefs?.['C/ColorD10'])
      body.style.setProperty('--color39', prefs?.['C/ColorC12'])
      body.style.setProperty('--color40', prefs?.['C/ColorF01'])
      body.style.setProperty('--color41', prefs?.['C/ColorF02'])
      body.style.setProperty('--color42', prefs?.['C/ColorF03'])
      body.style.setProperty('--color43', prefs?.['C/ColorF04'])
      body.style.setProperty('--color44', prefs?.['C/ColorF05'])
      body.style.setProperty('--color45', prefs?.['C/ColorF06'])
      body.style.setProperty('--color46', prefs?.['C/ColorF10'])
      body.style.setProperty('--color47', prefs?.['C/ColorF11'])
      body.style.setProperty('--color48', prefs?.['C/ColorE07'])
      body.style.setProperty('--color49', prefs?.['COLORTEXTOBISAGRA'])
      setCameraPosition(prefs?.['ZOOM2D'])
      if (
        prefs?.CATALOGOUNIVERSAL &&
        prefs?.UNIDADSUELO &&
        prefs?.UNIDADPARED0 &&
        prefs?.UNIDADPARED1 &&
        prefs?.UNIDADTECHO &&
        prefs?.UNIDADTABIQUE0 &&
        prefs?.UNIDADTABIQUE1 &&
        prefs?.UNIDADTABIQUE2 &&
        prefs?.UNIDADTABIQUE3 &&
        prefs?.UNIDADTABIQUE4 &&
        prefs?.UNIDADTABIQUE5
      )
        return
      setTextures(
        prefs?.CATALOGOUNIVERSAL,
        prefs?.UNIDADSUELO,
        prefs?.UNIDADPARED0,
        prefs?.UNIDADPARED1,
        prefs?.UNIDADTECHO,
        prefs?.UNIDADTABIQUE0,
        prefs?.UNIDADTABIQUE1,
        prefs?.UNIDADTABIQUE2,
        prefs?.UNIDADTABIQUE3,
        prefs?.UNIDADTABIQUE4,
        prefs?.UNIDADTABIQUE5,
      )
    })
    function logOutOnClose(user, setUser) {
      const remember = localStorage.getItem('remember_session')
      const isUserLogged = localStorage.getItem('isUserLogged')
      if (!remember && isUserLogged) {
        logOut(user, setUser)
      }
    }
    window.addEventListener('beforeunload', logOutOnClose.bind(null, userState[0], userState[1]))
    return () => {
      window.removeEventListener(
        'beforeunload',
        logOutOnClose.bind(null, userState[0], userState[1]),
      )
    }
  }, [])

  // Este efecto asegura que tipoUsuario siempre tenga valor
  React.useEffect(() => {
    const item = window.localStorage.getItem('userData')
    const fromSession = sessionStorage.getItem('tipoUsuario')
    let tipo = fromSession || 'P'
    if (item) {
      try {
        const userData = JSON.parse(item)
        if (userData?.tipoUsuario) {
          tipo = userData.tipoUsuario
        }
      } catch {}
    }
    useAppStore.getState().setTipoUsuario(tipo)
    sessionStorage.setItem('tipoUsuario', tipo)
  }, [])

  useUserLanguage(userState)
  return <userContext.Provider value={userState}>{children}</userContext.Provider>
}

function addLogoutOnClose(user: UserType, setUser: SetUserType) {}

async function refreshUserData(
  user: UserType,
  setUser: SetUserType,
  data: Store['textos']['idioma'] & { idIdioma: number; idioma: { id: number } },
  navigate?,
  href?,
) {
  const baseURL = getProxyUrl('api/Auth/getUserAct')
  const token = sessionStorage.getItem('access_token')
  const proxy = await fetch(baseURL, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Accept-Language': 'ES',
      'Content-Type': 'application/json',
      Authorization: 'Ninpo ' + token,
    },
  })
  const proxyData = await proxy.json()

  delete proxyData.passwordActual
  delete proxyData.passwordConfirmar
  delete proxyData.passwordNuevo
  window.localStorage.removeItem('userData')
  window.localStorage.setItem('userData', JSON.stringify(proxyData))

  window.localStorage.setItem('isUserLogged', 'true')
  setUser((prev) => ({
    ...prev,
    ...proxyData,
    idIdioma: proxyData.idIdioma,
    nombreCultura: getLanguageID('ID', proxyData.idIdioma),
    idiomaCultura: getLanguageID('ID', proxyData.idIdioma),
  }))
  // Setear idTiendas en el store global
  useAppStore.getState().setIdTiendas(proxyData.idTiendas ?? null)
  // Setear permisos en el store global
  useAppStore.getState().setDisVerdis(proxyData.disVerdis ?? false)
  useAppStore.getState().setDisVerUsu(proxyData.disVerUsu ?? false)
  useAppStore.getState().setDisVerGeneral(proxyData.disVerGeneral ?? false)
  useAppStore.getState().setVerPrecios(proxyData.verPrecios ?? false)
  useAppStore.getState().setAdmModPermisos(proxyData.admModPermisos ?? false)
  useAppStore.getState().setAsignarDisenyosUf(proxyData.asignarDisenyosUf ?? false)

  // Siempre llevar a /planner/saveProject después del login
  if (href?.includes('/planner')) {
    sessionStorage.setItem('pendingSave', 'true')
    localStorage.setItem('pendingAction', 'saveProject')
    localStorage.setItem('pendingFrom', 'login')
    if (navigate) {
      navigate('/planner/saveProject')
    }
  } else if (navigate) {
    if (href) {
      navigate(href)
    } else {
      navigate('/planner')
    }
  }
}

async function updateUser(
  user: UserType,
  setUser: SetUserType,
  //TODO check
  data: UpdateUserData & { id: number; codigo: string; idioma: { id: number } },
  idiomaCultura: string,
) {
  const setTextos = useAppStore.getState().setTextos
  if (window.localStorage.getItem('isUserLogged')) {
    const proxy = await fetch(getProxyUrl('api/Auth/modifyUserAct'), {
      body: JSON.stringify(data),
      method: 'POST',
      mode: 'cors',
      headers: {
        'Accept-Language': idiomaCultura,
        'Content-Type': 'application/json',
        Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
      },
    })

    const proxyData = await proxy.json()
    if (proxyData.status === 500) {
      return { error: translateApiError(proxyData.detail, 'Error interno del servidor.') }
    }

    const isError = refreshUserData(user, setUser, proxy, null, null)
    if (isError) return isError

    const setIdiomaCultura = useAppStore.getState().setIdiomaCultura
    setIdiomaCultura(data.nombreCultura)
  } else {
    new I18NProxy().loadIdioma(data.idiomaCultura).then((data: any) => {
      window.localStorage.setItem(
        'userData',
        JSON.stringify({
          ...user,
          nombreCultura: data.idioma?.nombreCultura,
          idIdioma: data.idioma.id,
        }),
      )

      setTextos({ idioma: data.idioma, textos: data.textos })
      const setIdiomaCultura = useAppStore.getState().setIdiomaCultura
      setIdiomaCultura(data.idioma.nombreCultura)
      setUser((user) => ({
        ...user,
        nombreCultura: data.idioma.nombreCultura,
        idIdioma: data.idioma.id,
      }))
    })
  }
}

function logOut(user: UserType, setUser: SetUserType) {
  cleanAuthToken()
  window.localStorage.removeItem('isUserLogged')
  window.localStorage.removeItem('remember_session')
  window.localStorage.removeItem('userData')
  setUser(userDefaultValue)
  new AutenticateProxy().newSession().then(() => location.reload())
}

async function login(
  user: UserType,
  setUser: SetUserType,
  { correo, password, remember }: LoginData,
  setPrefs,
  navigate,
  href,
) {
  try {
    const response = await new AutenticateProxy().autenticate({
      correo,
      password,
      remember,
    })
    if (response && response.tipoUsuario) {
      useAppStore.getState().setTipoUsuario(response.tipoUsuario)
    } else {
      useAppStore.getState().setTipoUsuario('P')
    }
    if (!response.token) {
      if (response.status === 500) {
        return {
          error: translateApiError(response.detail, 'Error interno del servidor.'),
        }
      }
      return { error: 'Error en la autenticación.' }
    }
    // Guardar el token correcto en sessionStorage
    sessionStorage.setItem('access_token', response.token)
    setPrefs(response.prefs, response.prefsInfo)
    refreshUserData(user, setUser, response, navigate, href)
    sessionStorage.setItem('tipoUsuario', response.tipoUsuario)
  } catch (error) {
    useAppStore.getState().setTipoUsuario('P')
    return { error: 'Error al conectar con el servidor.' }
  }
}

/**
 * @type user: { name: "Public"} | {name: string
 * postcode: string,
 * mail: string,
 * address: string,
 * languageID: null | number,
 * phone: number
 * postcode: string,
 * mail: string,
 * address: string,
 * languageID: (null | number),
 * phone: number}
 *
 * @type actions: { logOut, login }
 * Devuelve un objeto que contiene user y actions.
 */
export function useUser() {
  const contextValue = useContext(userContext)

  if (contextValue === null) {
    return {
      user: { nombre: '', bloquearEdicion: false },
      userActions: {
        logOut: () => {},
        login: () => Promise.resolve(undefined),
        updateUser: () => Promise.resolve(undefined),
      },
    }
  }
  const [user, setUser]: [UserType, SetUserType] = contextValue

  const userActions = {
    logOut: () => logOut(user, setUser),
    login: (data: LoginData, setPrefs, navigate, href) =>
      login(user, setUser, data, setPrefs, navigate, href),
    updateUser: (data: UpdateUserData) => updateUser(user, setUser, data, user.idiomaCultura),
  }

  return { user, userActions }
}
